import * as errors from '@attain-apps/errors'

import * as models from '../../../../models'
import type { Query } from '../../query'
import { Ref } from '../../ref'
import type { Subquery } from '../../subquery'
import * as edge from '../edge'
import * as node from '../node'

import { determineIfResultsShouldBeSingleton } from './determine-if-results-should-be-singleton'

/**
 * Creates nodes for each {@link Subquery} in the given
 * {@link Query.subqueries}. Particularly, it handles siblings that use
 * {@link Subquery.using} and creating the necessary nodes and edges to support
 * that
 */
export function createSubqueryNodes(
  subqueries: Query['subqueries'],
  // NOTE: Parent info is required because a subquery cannot be created on its
  // own.
  {
    nodesCache,
    parentInfo,
    schema,
    variables,
  }: Required.Semi<Omit<node.Context, 'name'>, 'parentInfo'>,
): node.ForRecords.BindableSubqueryNodes {
  const nodes: Partial<Record<Subquery.Name, node.OfAnyType>> = {}
  const parentConnectionEdgeBases: edge.ForConnection.Base[] = []
  const entries = Object.entries(subqueries ?? {})
  let entry: [Subquery.Name, Subquery | undefined] | undefined

  while ((entry = entries.pop())) {
    const [name, subquery] = entry
    if (subquery === undefined) {
      // We can ignore subqueries that don't exist
      continue
    }

    /**
     * The Subquery {@link node.OfAnyType} node that is being
     * created.
     */
    let subqueryNode: node.OfAnyType
    /**
     * The Subquery connecting edge info that is used to connect the subquery to
     * the parent / sibling node.
     */
    const subqueryConnectionEdgeInfo: Omit<
      edge.ForConnection,
      'node' | 'commonEntity' | 'isSibling'
    > = {
      // setup default inheritance in case of hierarchy
      ref: Ref.WithDefaults.from(subquery.ref),
    }

    const { using } = subquery
    if (using) {
      /**
       * The subquery's {@link Subquery.using using} node.
       */
      const subqueryUsingNode = nodes[using]

      // If a subquery has a "using" property indicating a dependency on another
      // node, but the corresponding node has not been created yet, the entry is
      // moved to the end of the array. This ensures that the dependent node is
      // created before the subquery is processed.
      if (!subqueryUsingNode) {
        entries.unshift(entry)
        continue
      }

      /**
       * Adds connection edges to the specified node. If the adding node is a
       * group, all entities within the group are connected to the same parent
       * node.
       */
      function _addEdgesToUsingRecordsNode({
        usingNode,
        usedByNode,
      }: {
        /**
         * The {@link node.ForRecords Node} to which connection edges are being added.
         */
        usingNode: node.ForRecords
        /**
         * The node, that holds connection edges that are being added.
         */
        usedByNode: node.OfAnyType
      }): void {
        const members =
          usedByNode instanceof node.ForGroup
            ? usedByNode.members
            : [usedByNode]

        // Connect the child nodes to their parent node by adding connecting
        // edges. If the child node is a group, all entities within the
        // group are connected to the same parent node.
        usingNode.edges.connecting.add(
          ...members.filter(node.ForRecords.isForRecords).map(member =>
            edge.ForConnection.from(schema, usingNode, {
              ...subqueryConnectionEdgeInfo,
              isSibling: true,
              node: member,
            }),
          ),
        )

        // TODO: only add if actually referenced
        usedByNode.edges.consuming.add({
          // TODO: make `true` if required in the filter
          isRequired: false,
          node: usingNode,
          resultsAsSingleton: determineIfResultsShouldBeSingleton({
            consumedByNode: usedByNode,
            node: usingNode,
            schema,
          }),
        })
      }

      // At this point, the usingNode exists, and the subquery has a reference
      // to it using the using property.

      if (subqueryUsingNode instanceof node.ForGroup) {
        // If the subquery using node is a group, create a new node for the group.
        // For each group member, generate a new Graph Node recursively.
        subqueryNode = new node.ForGroup({
          id: node.Id.create({ name, parentInfo }),
          members: subqueryUsingNode.members
            .filter(node.ForRecords.isForRecords)
            .flatMap(usingMemberNode => {
              const subqueryMemberNode = node.from(subquery, {
                name,
                nodesCache,
                parentInfo: usingMemberNode,
                schema,
                subqueryNodes: {
                  [using]: subqueryUsingNode,
                },
                variables,
              })

              _addEdgesToUsingRecordsNode({
                usedByNode: subqueryMemberNode,
                usingNode: usingMemberNode,
              })

              return subqueryMemberNode instanceof node.ForGroup
                ? subqueryMemberNode.members
                : subqueryMemberNode
            }),
          nodesCache,
          query: subquery,
          schema,
        })
      } else if (subqueryUsingNode instanceof node.ForRecords) {
        // If the subquery using node is of type 'ForRecords', we create the
        // subquery node and add the edges between it and the using node.
        subqueryNode = node.from(subquery, {
          name,
          nodesCache,
          parentInfo,
          schema,
          subqueryNodes: {
            [using]: subqueryUsingNode,
          },
          variables,
        })
        _addEdgesToUsingRecordsNode({
          usedByNode: subqueryNode,
          usingNode: subqueryUsingNode,
        })
      } else {
        // We don't know (as of 7/13/2025) how to handle any other type of using
        // node
        throw new errors.ValidationError({
          reason: 'Unsupported type',
          type: "'using' subquery node",
          value: subqueryUsingNode.id,
        })
      }
    } else {
      // If we are not using a sibling node, then we can just create the
      // subquery node
      subqueryNode = node.from(subquery, {
        name,
        nodesCache,
        parentInfo,
        schema,
        variables,
      })
      // And we can store information to connect to the parent node later
      parentConnectionEdgeBases.push(
        // If the subquery node is 'ForRecords', we will just connect that node.
        // If the subquery is 'ForGroup', we will connect all 'ForRecords' nodes
        // within the group.
        ...(subqueryNode instanceof node.ForRecords
          ? [{ node: subqueryNode }]
          : subqueryNode instanceof node.ForGroup
            ? subqueryNode.members
                .filter(node.ForRecords.isForRecords)
                .map(_node => ({ node: _node }))
            : []
        ).map(_edge => ({
          ...subqueryConnectionEdgeInfo,
          ..._edge,
          isSibling: false,
        })),
      )
    }

    // Stores the new subquery node so it can be referenced by other nodes
    nodes[name] = subqueryNode
  }

  // Used to connect to parent once it is created
  return {
    bind: parent => {
      const parentDimensionId = parent.source.modelId
        ? models.Dimension.Id.from(parent.source.modelId)
        : undefined

      if (
        // If parent has a dimension
        parentDimensionId &&
        parentConnectionEdgeBases.some(
          _edge =>
            // If edge doesn't have a ref
            !_edge.ref &&
            // And it is not in the same dimension as the parent
            _edge.node.source.modelId &&
            models.Dimension.Id.from(_edge.node.source.modelId) !==
              parentDimensionId,
        )
      ) {
        // Then we don't know how to connect it to the parent
        throw new errors.ValidationError({
          reason: "One or more subqueries are missing 'ref' property.",
        })
      }
      // Adding connections to parent node
      parent.edges.connecting.add(
        ...parentConnectionEdgeBases.map(parentConnectionEdgeBase =>
          edge.ForConnection.from(schema, parent, parentConnectionEdgeBase),
        ),
      )
    },
    nodes,
  }
}
